// The engine: pure, synchronous, no I/O. Every decision runs the tenant gate before any policy
// rule; a missing tenant on either side is its own deny reason and nothing can pass it.
import { isAction, ACTIONS } from './actions.js';
import { ALLOWED, deny } from './decision.js';
import { PLATFORM_TENANT, tenantGrant } from './principal.js';
import { evaluate, possible, scopeOf } from './policy/rules.js';
import { ORDINARY_FIELDS } from './policy/types.js';
import { defaultPolicies } from './policies/index.js';
export const ROUTE_AUTH_KINDS = ['tenant', 'platform-admin', 'public'];
/** The verb ordinary writes go through when a policy does not name one. */
function defaultWriteVerb(verbs) {
    for (const candidate of ['write', 'update'])
        if (verbs.includes(candidate))
            return candidate;
    return undefined;
}
export function createEngine(policies) {
    function resolve(action, kind) {
        if (!isAction(action))
            return 'unknown-action';
        const [actionKind, verb] = action.split(':');
        if (kind !== undefined && kind !== actionKind)
            return 'unknown-action';
        const policy = policies[actionKind];
        if (!policy)
            return 'unknown-resource';
        const rule = policy.actions[verb];
        if (!rule)
            return 'unknown-action';
        return { policy, rule, verb };
    }
    /** The tenant gate. Runs before every rule; nothing bypasses it. */
    function gate(policy, principal, resourceTenantId) {
        if (policy.scope === 'public')
            return undefined;
        if (principal.tenantId === undefined || resourceTenantId === undefined)
            return 'tenant-missing';
        if (policy.scope === 'platform') {
            if (resourceTenantId !== PLATFORM_TENANT)
                return 'platform-scope';
            return principal.tenantId === PLATFORM_TENANT ? undefined : 'tenant-scope';
        }
        if (resourceTenantId === PLATFORM_TENANT)
            return 'platform-scope';
        if (principal.tenantId === resourceTenantId)
            return undefined;
        return principal.grants.has(tenantGrant(resourceTenantId)) ? undefined : 'tenant-scope';
    }
    function can(principal, action, resource) {
        const resolved = resolve(action, resource.kind);
        if (typeof resolved === 'string')
            return deny(resolved);
        const blocked = gate(resolved.policy, principal, resource.tenantId);
        if (blocked)
            return deny(blocked);
        return evaluate(resolved.rule, principal, resource) ? ALLOWED : deny('not-permitted');
    }
    function capabilities(principal, resource) {
        const out = {};
        for (const verb of ACTIONS[resource.kind]) {
            const action = `${resource.kind}:${verb}`;
            out[action] = can(principal, action, resource).allowed;
        }
        return out;
    }
    function scopeFilter(principal, action, tenantId = principal.tenantId) {
        const resolved = resolve(action);
        if (typeof resolved === 'string')
            return { allowed: false, reason: resolved };
        const blocked = gate(resolved.policy, principal, tenantId);
        if (blocked)
            return { allowed: false, reason: blocked };
        const listTenant = tenantId ?? PLATFORM_TENANT;
        const scope = scopeOf(resolved.rule, principal, listTenant);
        switch (scope.kind) {
            case 'all':
                return { allowed: true, tenantId: listTenant };
            case 'none':
                return { allowed: false, reason: 'not-permitted' };
            case 'unscopable':
                return { allowed: false, reason: 'unscopable' };
            case 'some':
                return {
                    allowed: true,
                    tenantId: listTenant,
                    ...(scope.offices.size > 0 ? { offices: [...scope.offices].sort() } : {}),
                    ...(scope.agentCode !== undefined ? { agentCode: scope.agentCode } : {}),
                    ...(scope.officeless ? { officeless: true } : {}),
                };
        }
    }
    function writableFields(principal, resource) {
        const policy = policies[resource.kind];
        const writeVerb = policy.writeAction ?? defaultWriteVerb(ACTIONS[resource.kind]);
        if (writeVerb === undefined)
            return [];
        const writeAction = `${resource.kind}:${writeVerb}`;
        if (!can(principal, writeAction, resource).allowed)
            return [];
        if (!policy.fields)
            return [ORDINARY_FIELDS];
        const fields = [];
        for (const [field, rule] of Object.entries(policy.fields)) {
            if (evaluate(rule, principal, resource))
                fields.push(field);
        }
        return fields;
    }
    function routeGate(principal, auth) {
        if (principal.tenantId === undefined)
            return 'tenant-missing';
        if (auth === 'platform-admin') {
            return principal.tenantId === PLATFORM_TENANT && principal.roles.includes('PlatformAdmin')
                ? undefined
                : 'route-auth';
        }
        // A tenant route needs a tenant context: a membership, or a support session's tenant grant.
        if (principal.tenantId !== PLATFORM_TENANT)
            return undefined;
        return [...principal.grants].some((g) => g.startsWith('tenant#')) ? undefined : 'route-auth';
    }
    function canAccessRoute(principal, route) {
        if (route.auth === 'public')
            return ALLOWED;
        const blocked = routeGate(principal, route.auth);
        if (blocked)
            return deny(blocked);
        const resolved = resolve(route.action);
        if (typeof resolved === 'string')
            return deny(resolved);
        // A platform principal on a tenant route is a visitor: only grant-aware rules can apply.
        const visitor = route.auth === 'tenant' && principal.tenantId === PLATFORM_TENANT;
        return possible(resolved.rule, principal, visitor) ? ALLOWED : deny('not-permitted');
    }
    function visibleMenu(principal, menu) {
        const out = [];
        for (const item of menu) {
            const ownAllowed = item.action === undefined ||
                canAccessRoute(principal, { auth: item.auth ?? 'tenant', action: item.action }).allowed;
            if (!ownAllowed)
                continue;
            if (item.children === undefined) {
                out.push(item);
                continue;
            }
            const children = visibleMenu(principal, item.children);
            // A group is only its children: with none left it disappears. An item that has both an
            // action and children survives on its own action (a hub page with an empty submenu).
            if (children.length === 0 && item.action === undefined)
                continue;
            out.push({ ...item, children });
        }
        return out;
    }
    return { can, capabilities, scopeFilter, writableFields, canAccessRoute, visibleMenu };
}
export const engine = createEngine(defaultPolicies);
export const can = (principal, action, resource) => engine.can(principal, action, resource);
export const capabilities = (principal, resource) => engine.capabilities(principal, resource);
export const scopeFilter = (principal, action, tenantId) => engine.scopeFilter(principal, action, tenantId);
export const writableFields = (principal, resource) => engine.writableFields(principal, resource);
export const canAccessRoute = (principal, route) => engine.canAccessRoute(principal, route);
export const visibleMenu = (principal, menu) => engine.visibleMenu(principal, menu);
/** Whether a field is writable given `writableFields` output: privileged fields must be named. */
export function isFieldWritable(writable, field, privileged = PRIVILEGED_FIELDS) {
    if (privileged.has(field))
        return writable.includes(field);
    return writable.includes(ORDINARY_FIELDS) || writable.includes(field);
}
/** Fields that need their own rule across the shipped policies. */
export const PRIVILEGED_FIELDS = new Set(Object.values(defaultPolicies).flatMap((p) => Object.keys(p.fields ?? {}).filter((f) => f !== ORDINARY_FIELDS)));
//# sourceMappingURL=engine.js.map